import type { Expression, FuseOptionKey } from 'fuse.js';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useRef } from 'react';

export type FuseSearchField<T> = {
  /** The field name on T to search */
  name: FuseOptionKey<T>;
  /** If true, uses exact-match prefix syntax ('query) instead of fuzzy */
  exact?: boolean;
};

export type ParsedQuery = {
  /** `"phrase"` — must appear as whole words */
  quoted: string[];
  /** bare terms — must appear, matched by Fuse */
  unquoted: string[];
  /** `-term` — must not appear anywhere */
  excluded: string[];
  /** `-"phrase"` — must not appear as whole words */
  excludedQuoted: string[];
};

export function parseQuery(query: string): ParsedQuery {
  const quoted: string[] = [];
  const excludedQuoted: string[] = [];
  const remaining = query
    // A '-' immediately before the opening quote negates the phrase.
    .replace(/(-?)"([^"]+)"/g, (_, minus: string, phrase: string) => {
      (minus ? excludedQuoted : quoted).push(phrase);

      return '';
    })
    .replace(/"/g, '');

  const excluded: string[] = [];
  const unquoted: string[] = [];
  for (const term of remaining.split(' ').filter((q) => q !== '')) {
    // Only a leading '-' negates, so hyphenated terms ("Non-alcoholic") and a
    // lone '-' the user is still typing stay out of the way.
    if (!term.startsWith('-')) {
      unquoted.push(term);
      continue;
    }

    const negated = term.slice(1);
    if (negated !== '') {
      excluded.push(negated);
    }
  }

  return { quoted, unquoted, excluded, excludedQuoted };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function substringRegExp(term: string): RegExp {
  return new RegExp(escapeRegExp(term), 'i');
}

function wholeWordRegExp(phrase: string): RegExp {
  return new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'i');
}

const matchesEvery = (regexes: RegExp[], values: string[]) =>
  regexes.every((regex) => values.some((value) => regex.test(value)));

const matchesSome = (regexes: RegExp[], values: string[]) =>
  regexes.some((regex) => values.some((value) => regex.test(value)));

/**
 * Collects the searchable values at a Fuse key path. Handles the dotted,
 * array-valued paths Fuse itself supports (`items.product` on a receipt spans
 * every line item) — a plain `item[path]` lookup returns undefined for those.
 *
 * Fuse's own walker is reachable as `Fuse.config.getFn`, but it returns
 * `{ v, i }` index entries rather than plain strings for array paths, so
 * reusing it would mean depending on that internal shape.
 */
function getFieldValues(item: unknown, path: string): string[] {
  // Overwhelmingly the common case: a top-level key holding a string. Anything
  // else — nested paths, arrays, numbers — takes the walk below.
  const direct = (item as Record<string, unknown>)[path];
  if (typeof direct === 'string') {
    return [direct];
  }

  const values = path.split('.').reduce<unknown[]>(
    // flatMap flattens the array a segment lands on, so the next segment reads
    // each entry in turn.
    (current, segment) =>
      current.flatMap((value) =>
        value !== null && typeof value === 'object'
          ? ((value as Record<string, unknown>)[segment] ?? [])
          : []
      ),
    [item]
  );

  return values.filter((value) => value !== null && value !== undefined).map(String);
}

/**
 * Holds the `items` reference steady for as long as its elements are unchanged.
 *
 * Callers hand this hook a freshly built array far more often than the
 * searchable text actually changes — the order editor derives its product list
 * as `orderItems.map((oi) => oi.product)`, so every quantity keystroke produces
 * a new array of the same products, and a new array reindexes the whole catalog.
 *
 * Element identity is the right proxy for content because the data layer
 * replaces items rather than mutating them: an item whose fields change arrives
 * as a new object and still busts the index, while an item that merely got
 * re-collected into a new array does not. That is the same assumption React
 * itself makes about this data.
 */
function useStableItems<T>(items: T[]): T[] {
  const stable = useRef(items);

  if (!sameElements(stable.current, items)) {
    stable.current = items;
  }

  return stable.current;
}

function sameElements<T>(a: T[], b: T[]): boolean {
  return a === b || (a.length === b.length && a.every((item, i) => item === b[i]));
}

/**
 * Generic Fuse.js search hook. Returns a filter function that tests whether
 * an item matches the given query across the configured fields.
 *
 * Supports quoted phrases for word-boundary exact matching (e.g. `"VS"` won't
 * match "VSOP") and `-term` / `-"phrase"` exclusions. Unquoted terms use
 * Fuse.js fuzzy/extended search.
 *
 * @param query - The search string (space-separated terms are ANDed, "quoted" terms use exact match, -terms are excluded)
 * @param items - The list of items to index
 * @param fields - The fields to search, with exact vs fuzzy per field
 * @param getItemKey - Returns a unique key for each item (used for the match set)
 */
export function useFuseSearch<T>(
  query: string | undefined,
  items: T[],
  fields: FuseSearchField<T>[],
  getItemKey: (item: T) => string | number
): (item: T) => boolean {
  const keys = useMemo(() => fields.map((f) => f.name), [fields]);
  const stableItems = useStableItems(items);

  const index = useMemo(() => {
    return new Fuse<T>(stableItems, {
      threshold: 0,
      ignoreLocation: true,
      useExtendedSearch: true,
      keys,
    });
  }, [stableItems, keys]);

  const matchingKeys = useMemo(() => {
    if (!query || query === '') {
      return new Set(stableItems.map(getItemKey));
    }

    const { quoted, unquoted, excluded, excludedQuoted } = parseQuery(query);

    function orExpression(q: string): Expression {
      return {
        $or: fields.map((f) => ({
          [f.name as string]: f.exact ? `'${q}` : q,
        })),
      };
    }

    const allTerms = [...unquoted, ...quoted];
    const candidates =
      allTerms.length === 0
        ? stableItems
        : index.search({ $and: allTerms.map(orExpression) }).map((result) => result.item);

    // Whole-word matching has no Fuse equivalent — extended search has no
    // \b-anchored operator — so quoted phrases have always been post-filtered.
    // Exclusions join them there. Fuse *can* express a bare `-term` as an $and
    // of per-field `!term` (an $or would be true of nearly every item), but
    // that would make the user's own ! ^ $ = ' characters operators mid-word;
    // `exact:` fields exist to prevent exactly that, and there is no
    // equivalent escape for the inverse operators.
    const required = quoted.map(wholeWordRegExp);
    const forbidden = [...excluded.map(substringRegExp), ...excludedQuoted.map(wholeWordRegExp)];

    // Not just a shortcut for the empty case: it keeps the common query — plain
    // terms, no phrases or exclusions — from reading any field values at all.
    if (required.length === 0 && forbidden.length === 0) {
      return new Set(candidates.map(getItemKey));
    }

    const fieldNames = fields.map((f) => f.name as string);

    return new Set(
      candidates
        .filter((item) => {
          const values = fieldNames.flatMap((name) => getFieldValues(item, name));

          return matchesEvery(required, values) && !matchesSome(forbidden, values);
        })
        .map(getItemKey)
    );
  }, [query, index, stableItems, fields, getItemKey]);

  return useCallback((item: T) => matchingKeys.has(getItemKey(item)), [matchingKeys, getItemKey]);
}
